/**
 * KORAs Hinweise unter der Vorgangsliste (v11, Abschnitt 6).
 *
 * Bis v7 schwebte ein Satz über der Maske und ließ sich wegklicken. Er stand
 * im Weg, sagte etwas über den ganzen Tag und man wusste nicht, in welcher
 * Kachel der genannte Vorgang liegt. Jetzt steht KORA unter der Liste der
 * geöffneten Rubrik und spricht nur über diese Tätigkeit — mit Knopf
 * „Erledigt" statt Kreuz.
 *
 * Erledigt wird auch ohne Klick: Wer den Vorgang öffnet, um den es geht, hat
 * den Hinweis befolgt. Ein zweiter Klick dafür wäre eine Aufgabe, die
 * niemand will.
 *
 * Drei Stufen, drei Farben: ruhig und erinnernd in Orange, mahnend in
 * Danger. Was nicht mahnt – „versandfertig", „ruht" – bleibt auf Stufe 1 und
 * wird nie rot.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import type { KoraHinweise as Hinweise } from '../typen';
import './korahinweise.css';

export function KoraHinweise({
  maske = 'mein-tag',
  rubrik,
  rubrikLabel,
  onOeffnen,
}: {
  /** Welche Maske spricht – „mein-tag", „angebote", „auftraege", … */
  maske?: string;
  /**
   * Schlüssel der geöffneten Kachel, oder „alle". Außerhalb von „Mein Tag"
   * gibt es keine Rubriken; dort bleibt es bei „alle".
   */
  rubrik: string;
  rubrikLabel: string;
  /** Den Vorgang öffnen, um den es geht. */
  onOeffnen: (angebotId: number) => void;
}) {
  const queryClient = useQueryClient();

  const abfrage = useQuery({
    queryKey: ['kora-hinweise', maske, rubrik],
    queryFn: () =>
      api.hole<Hinweise>(`/kora/hinweise?maske=${encodeURIComponent(maske)}&rubrik=${encodeURIComponent(rubrik)}`),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const erledigt = useMutation({
    mutationFn: (id: string) => api.sende<{ ok: boolean }>(`/kora/hinweise/${id}/erledigt`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['kora-hinweise'] }),
  });

  const zurueck = useMutation({
    mutationFn: () => api.sende<{ ok: boolean }>('/kora/hinweise/zuruecknehmen', { maske, rubrik }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['kora-hinweise'] }),
  });

  const d = abfrage.data;
  if (!d || (d.hinweise.length === 0 && d.erledigt === 0)) return null;

  const offene = d.hinweise.filter((h) => !h.erledigtUm);

  return (
    <section className="kh">
      <div className="kh-kopf">
        <span className="kh-kicker">Kora · Hinweise zu {rubrikLabel}</span>
        <span className="kh-zaehler">
          {d.offen === 0
            ? 'nichts offen'
            : `${d.offen} ${d.offen === 1 ? 'Hinweis' : 'Hinweise'}`}
          {/*
            Höchstens drei Karten gleichzeitig; was darüber hinaus ansteht,
            steht als Zahl hier. Fünf Hinweise untereinander liest niemand.
          */}
          {d.weitere > 0 ? ` · ${d.weitere} weitere` : ''}
          {d.erledigt > 0 ? ` · ${d.erledigt} erledigt` : ''}
        </span>
      </div>

      {offene.map((h) => (
        <article className={`kh-karte stufe${h.stufe}`} key={h.id}>
          <span className="kh-portrait">
            <img src={h.bild} alt={`Kora, Stimmung ${h.pose}`} />
          </span>
          <div className="kh-text">
            <div className="kh-zeile">
              <span className="kh-marke">{rubrikLabel}</span>
              {h.stufe > 1 && <span className="kh-stufe">{h.stufe}. Erinnerung</span>}
              <span className="kh-pose">{h.pose}</span>
            </div>
            <p className="kh-satz">{h.text}</p>
          </div>
          <div className="kh-aktionen">
            {h.vorgangId !== null && (
              <button
                type="button"
                className="kh-vorgang"
                title={`${h.vorgangName} öffnen – erledigt den Hinweis`}
                onClick={() => {
                  erledigt.mutate(h.id);
                  onOeffnen(h.vorgangId!);
                }}
              >
                {h.vorgangName}
              </button>
            )}
            <button
              type="button"
              className="kh-erledigt"
              onClick={() => erledigt.mutate(h.id)}
              disabled={erledigt.isPending}
            >
              Erledigt
            </button>
          </div>
        </article>
      ))}

      {d.erledigt > 0 && (
        <div className="kh-abgehakt">
          <span>
            {d.erledigt === 1 ? 'Ein Hinweis erledigt' : `${d.erledigt} Hinweise erledigt`}
            {d.zuletzt ? ` · zuletzt um ${d.zuletzt}` : ''}
          </span>
          <button type="button" className="kh-nochmal" onClick={() => zurueck.mutate()}>
            nochmal ansehen
          </button>
        </div>
      )}
    </section>
  );
}

/**
 * Derselbe Block für die übrigen Masken (v11, 10.2 Regel 6).
 *
 * Bis v11 schwebte KORA dort unten rechts als Pille mit einem Kreuz. Der
 * Platz war überall gleich – das war der Gewinn –, aber der Satz lag über
 * dem Inhalt, ließ sich nur wegklicken statt erledigen und war nach dem
 * nächsten Laden wieder da. Jetzt steht er unter dem Inhalt der Maske, mit
 * denselben drei Stufen und demselben Knopf „Erledigt" wie auf „Mein Tag".
 *
 * Der Vorgangsklick führt immer ins Angebot-Detail: Dort hängt auch die
 * Abwicklung dran, und die Meldungen nennen den Vorgang, nicht die Zeile.
 */
export function KoraMaskenhinweise({ maske, label }: { maske: string; label: string }) {
  const navigate = useNavigate();
  return (
    <KoraHinweise
      maske={maske}
      rubrik="alle"
      rubrikLabel={label}
      onOeffnen={(id) => navigate(`/angebot/${id}`)}
    />
  );
}
